import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { CheckCircle2, AlertCircle, Clock, Wallet, Home } from "lucide-react";

const mpBackendBase =
  process.env.REACT_APP_MP_BACKEND_URL ||
  process.env.REACT_APP_BACK_URL ||
  `${window.location.protocol}//${window.location.hostname}:5000`;

export default function PaymentCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const params = new URLSearchParams(location.search);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const status =
    params.get("status") ||
    params.get("collection_status") ||
    (location.pathname.includes("success") ? "approved" : "failure");
  const paymentId = params.get("payment_id") || params.get("collection_id");
  const preferenceId = params.get("preference_id");
  const externalReference = params.get("external_reference");

  const isSuccess = status?.toLowerCase().includes("success") || status === "approved";
  const isFailure = status?.toLowerCase().includes("fail");
  const isPending = status?.toLowerCase().includes("pending");

  useEffect(() => {
    const procesar = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = await getAccessTokenSilently().catch(() => null);
        await fetch(`${mpBackendBase}/procesar-pago`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            status,
            paymentId,
            preferenceId,
            externalReference,
          }),
        });
        setStatusMsg("Estado de pago actualizado.");
      } catch (err: any) {
        setError(err?.message || "No se pudo procesar el pago.");
      } finally {
        setLoading(false);
      }
    };
    procesar();
  }, [status, paymentId, preferenceId, externalReference, getAccessTokenSilently]);

  const icon = isSuccess ? (
    <CheckCircle2 className="h-10 w-10 text-emerald-600" />
  ) : isFailure ? (
    <AlertCircle className="h-10 w-10 text-rose-600" />
  ) : (
    <Clock className="h-10 w-10 text-amber-600" />
  );

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-6 shadow-xl space-y-3">
        <div className="flex items-center gap-3">
          {icon}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Resultado de pago
            </p>
            <h1 className="text-2xl font-bold text-slate-900">
              {isSuccess ? "Pago aprobado" : isFailure ? "Pago rechazado" : "Pago pendiente"}
            </h1>
          </div>
        </div>
        <div className="space-y-2 text-sm text-slate-700">
          <p>Estado: <span className="font-semibold">{status || "N/A"}</span></p>
          {paymentId && <p>ID de pago: <span className="font-semibold">{paymentId}</span></p>}
          {preferenceId && <p>Preferencia: <span className="font-semibold">{preferenceId}</span></p>}
          {externalReference && <p>Referencia: <span className="font-semibold">{externalReference}</span></p>}
          {statusMsg && <p className="text-emerald-600">{statusMsg}</p>}
          {error && (
            <p className="flex items-center gap-2 text-rose-700">
              <AlertCircle className="h-4 w-4" /> {error}
            </p>
          )}
          {loading && <p className="text-slate-500">Actualizando estado...</p>}
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate("/pagos")}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
          >
            <Wallet className="h-4 w-4" />
            Volver a pagos
          </button>
          <button
            onClick={() => navigate("/inicio")}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Home className="h-4 w-4" />
            Ir al inicio
          </button>
        </div>
      </div>
    </div>
  );
}
